import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardBody, Nav, NavItem, NavLink, TabPane, TabContent, Button, Collapse, Col, Row, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Select from 'react-select';
import classnames from 'classnames';
import { useAuth } from '../../../Context/Auth/AuthContext';
import PSO from '../../../services/PSO';
import { toast } from 'react-toastify';
import Strings from '../../../translations/Translations';
import Users from './components/Users';
import Photos from './components/Photos';
import Permissions from './components/Permissions';
import QuickApprove from './components/QuickApprove';
import EditVideo from './components/EditVideo';
import EditSong from './components/EditSong';
import EditStyle from './components/EditStyle';
import { Link } from 'react-router-dom';
import ShareComponent from '../../../Layout/Components/ShareComponent';
import RemixEmbedComponent from '../../../Layout/Components/RemixEmbedComponent';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import RemixExport from './components/RemixExport';

export default function ManageCampaignModule () {
  const { authenticated, toggleLoading, currentCampaign } = useAuth();

  const [activeTab, setActiveTab] = useState('1');
  const toggleTab = useCallback((tab) => {
    setActiveTab(tab)
  }, [setActiveTab]);

  const [playerOpen, setPlayerOpen] = useState(true);
  const editRef = useRef(null);
  const addPhotoRef = useRef(null);

  const scrollToEdit = useCallback(() => {
    editRef.current.scrollIntoView()
  }, [editRef]);

  const [selectedOption, setSelectedOption] = useState(null);
  const [options, setOptions] = useState([]);
  const [its, setIts] = useState();
  const [currentIt, setCurrentIt] = useState();

  const [shareModal, setShareModal] = useState(false);
  const toggleShareModal = useCallback(() => {
    setShareModal(!shareModal);
  }, [setShareModal, shareModal]);

  const changeIt = useCallback((evt) => {
    setSelectedOption(evt);
    if (its && its.length > 0) {
      const selectedIts = its.filter((value) => {
        if (value.rid === evt.value) {
          return value;
        }
        return false;
      });
      if (selectedIts && selectedIts.length > 0) {
        setCurrentIt(selectedIts[0]);
      } else {
        setCurrentIt();
      }

      setPlayerOpen(true);
    }
  }, [setSelectedOption, its, setCurrentIt, setPlayerOpen]);

  const loadLists = useCallback((localIts) => {
    setIts(localIts);
    const optionsToSet = localIts.map((value) => {
      return {
        value: value.rid,
        label: value.rti
      };
    });
    setOptions(optionsToSet);
  }, [setIts]);

  const getVideos = useCallback(async () => {
    toggleLoading(true);

    try {
      const response = await PSO.getRemixListByCollection({
        un: authenticated.un,
        al: authenticated.al,
        colid: currentCampaign.colid
      });

      if (response.result === 'success') {
        loadLists(response.its);
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, toggleLoading, loadLists, currentCampaign]);

  useEffect(() => {
    getVideos();
  }, [getVideos]);

  return (
    <Fragment>
      <Card className="mb-3">
        <CardHeader className="card-header-tab z-index-6">
          <div className="card-header-title font-size-lg text-transform-none font-weight-normal">
            Video(s):
            <span className="d-inline-block ml-2" style={{ width: 250 }}>
              <Select
                value={selectedOption}
                onChange={changeIt}
                options={options}
              />
            </span>
          </div>
        </CardHeader>
      </Card>

      {/* Video Player */}
      {selectedOption && (
        <Card className='mb-3'>
          <CardHeader id="headingOne">
              <Button
                block
                color='link'
                className="text-left m-0 p-0"
                onClick={() => setPlayerOpen(!playerOpen)}
                aria-expanded={playerOpen}
                aria-controls="collapseOne"
              >
                <h5 className="m-0 p-0 pr-2 d-flex justify-content-between">
                  {Strings('pepblast_video')}
                  {playerOpen && <IoIosArrowUp />}
                  {!playerOpen && <IoIosArrowDown />}
                </h5>
              </Button>
          </CardHeader>
          <Collapse
            isOpen={playerOpen}
            data-parent="#accordion"
            id="collapseOne"
            aria-labelledby="headingOne"
          >
            <CardBody>
            {currentIt && currentIt.rid && (
              <Col md={12}>
                <RemixEmbedComponent currentIt={currentIt} />
              </Col>
            )}
            </CardBody>
          </Collapse>
        </Card>
      )}

      {/* Tasks */}
      {selectedOption && (
        <Card className="mb-3">
          <CardHeader>
            {Strings('tasks')}
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={12}>
                <Button
                  color='primary'
                  className='mr-2'
                  onClick={() => {
                    try {
                      addPhotoRef.current.click();
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  {Strings('add_photo')}
                </Button>
                <Button
                  color='primary'
                  className='mr-2'
                  onClick={toggleShareModal}
                >
                  {Strings('share')}
                </Button>
                <Button
                  color='primary'
                  className='mr-2'
                  onClick={scrollToEdit}
                >
                  {Strings('edit')}
                </Button>
                {currentIt && currentIt.rid && (
                  <Link
                    className='btn btn-primary mr-2'
                    to={`/campaign/analyze?rid=${currentIt.rid}`}
                  >
                    {Strings('analyze')}
                  </Link>
                )}
                <RemixExport label={Strings('export_to_mp4')} remixPars={currentIt} />
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {/* Edit options */}
      {selectedOption && (
        <div ref={editRef}>
          <Card className="mb-3" >
            <CardHeader className='flex-column' style={{height: '6rem'}}>
              <div className='w-100 py-3'>
                {Strings('edit')}
              </div>
              <Nav justified>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '1' })}
                    onClick={() => toggleTab('1')}
                  >
                    {Strings('quick_approve')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '2' })}
                    onClick={() => toggleTab('2')}
                  >
                    {Strings('Users')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '3' })}
                    onClick={() => toggleTab('3')}
                  >
                    {Strings('photos')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '4' })}
                    onClick={() => toggleTab('4')}
                  >
                    {Strings('permissions')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '6' })}
                    onClick={() => toggleTab('6')}
                  >
                    {Strings('edit_song')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '7' })}
                    onClick={() => toggleTab('7')}
                  >
                    {Strings('edit_style')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink href="#"
                    className={classnames({ active: activeTab === '5' })}
                    onClick={() => toggleTab('5')}
                  >
                    {Strings('edit_details')}
                  </NavLink>
                </NavItem>
              </Nav>
            </CardHeader>
            <CardBody>

              <TabContent activeTab={activeTab}>

                <TabPane tabId="1">
                  <QuickApprove
                    currentIt={currentIt}
                  />
                </TabPane>

                <TabPane tabId="2">
                  <Users
                    currentIt={currentIt}
                  />
                </TabPane>

                <TabPane tabId="3">
                  <Photos
                    currentIt={currentIt}
                    addPhotoRef={addPhotoRef}
                  />
                </TabPane>

                <TabPane tabId="4">
                  <Permissions
                    currentIt={currentIt}
                  />
                </TabPane>

                <TabPane tabId="5">
                  <EditVideo
                    currentIt={currentIt}
                  />
                </TabPane>

                <TabPane tabId="6">
                  <EditSong
                    currentIt={currentIt} 
                  />
                </TabPane>

                <TabPane tabId="7">
                  <EditStyle
                    currentIt={currentIt} 
                  />
                </TabPane>

              </TabContent>

            </CardBody>
          </Card>
        </div>
      )}

      <Modal isOpen={shareModal} toggle={toggleShareModal}>
        <ModalHeader toggle={toggleShareModal}>
          {Strings('share_video')}
        </ModalHeader>
        <ModalBody>
          <ShareComponent currentIt={currentIt} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleShareModal}>
            {Strings('close')}
          </Button>
        </ModalFooter>
      </Modal>

    </Fragment>
  );
}
